import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const PROVIDER_LABELS = {
  stripe: 'Stripe',
  komoju: 'Komoju'
};

const BRAND_LABELS = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  jcb: 'JCB',
  link: 'Link',
  unknown: 'Unknown'
};

const CARD_CLASS = 'bg-white border border-[#e5e7eb] rounded-xl p-6 h-[390px] flex flex-col';
const toTitleCase = (value = '') =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

const PaymentMethodCard = ({ data }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('provider');

  if (!data || data.total === 0) {
    return (
      <div className={CARD_CLASS}>
        <p className="text-[15px] font-semibold text-[#353E4A] mb-5">
          {t('subscriptions.admin.overview.paymentMethods.title')}
        </p>
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 rounded-xl bg-[#F0F2F4] flex items-center justify-center mb-4">
            <svg className="w-5 h-5 text-[#B5BDCA]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" />
            </svg>
          </div>
          <p className="text-[13px] text-[#97A3B5]">
            {t('subscriptions.admin.overview.paymentMethods.empty')}
          </p>
        </div>
      </div>
    );
  }

  const { total, providers = [], methods = [] } = data;
  const isProviderView = viewMode === 'provider';

  return (
    <div className={CARD_CLASS}>
      <div className="flex items-center justify-between mb-5">
        <p className="text-[15px] font-semibold text-[#353E4A]">
          {t('subscriptions.admin.overview.paymentMethods.title')}
        </p>
        <span className="text-[12px] text-[#97A3B5]">
          {total} {t('subscriptions.admin.overview.total')}
        </span>
      </div>

      <div className="inline-flex items-center rounded-lg border border-[#D7DEE8] bg-[#F1F5F9] p-1 mb-4 self-start shadow-[inset_0_1px_1px_rgba(15,23,42,0.06)]">
        <button
          type="button"
          onClick={() => setViewMode('provider')}
          aria-pressed={isProviderView}
          className={`px-2.5 py-1.5 rounded-md text-[12px] font-semibold transition-all duration-150 ${
            isProviderView
              ? 'bg-[#334155] text-white shadow-[0_2px_6px_rgba(15,23,42,0.28)]'
              : 'text-[#64748B] hover:text-[#334155] hover:bg-white/70'
          }`}
        >
          {t('subscriptions.admin.overview.paymentMethods.byProvider')}
        </button>
        <button
          type="button"
          onClick={() => setViewMode('brand')}
          aria-pressed={!isProviderView}
          className={`px-2.5 py-1.5 rounded-md text-[12px] font-semibold transition-all duration-150 ${
            !isProviderView
              ? 'bg-[#334155] text-white shadow-[0_2px_6px_rgba(15,23,42,0.28)]'
              : 'text-[#64748B] hover:text-[#334155] hover:bg-white/70'
          }`}
        >
          {t('subscriptions.admin.overview.paymentMethods.byBrand')}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="space-y-2">
          {isProviderView
            ? providers.map((p) => (
                <div key={p.provider} className="flex items-center justify-between py-1.5">
                  <span className="text-[13px] text-[#353E4A]">
                    {PROVIDER_LABELS[p.provider] || p.provider}
                  </span>
                  <span className="text-[13px] font-medium text-[#353E4A]">{p.count}</span>
                </div>
              ))
            : methods.map((m) => (
                <div key={`${m.type}-${m.brand}`} className="flex items-center justify-between py-1.5">
                  <span className="text-[13px] text-[#353E4A]">
                    {m.brand === 'unknown'
                      ? toTitleCase(m.type)
                      : BRAND_LABELS[m.brand] || toTitleCase(m.brand)}
                    {m.brand !== 'unknown' && m.type !== 'card' && m.type !== 'unknown' && (
                      <span className="text-[11px] text-[#97A3B5] ml-1.5">({toTitleCase(m.type)})</span>
                    )}
                  </span>
                  <span className="text-[13px] font-medium text-[#353E4A]">{m.count}</span>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodCard;
